import type { FullWeaponInfo, WeaponBars, WeaponId } from "../../../api/types";
import { Badge, LockBadge } from "../../components/Badge";
import { OptionCard } from "../../components/OptionCard";
import { BAR_ROWS, FIRE_MODE_LABEL } from "./labels";

function MiniBars({ bars }: { bars: WeaponBars }) {
  return (
    <span class="loadouts__mini" aria-hidden="true">
      {BAR_ROWS.map(({ key, label }) => {
        const value = Math.min(100, Math.max(0, bars[key]));
        return (
          <span key={key} class="loadouts__mini-row">
            <span class="loadouts__mini-label">{label}</span>
            <span class="loadouts__mini-track">
              <span class="loadouts__mini-fill" style={{ width: `${value}%` }} />
            </span>
            <span class="loadouts__mini-value">{Math.round(value)}</span>
          </span>
        );
      })}
    </span>
  );
}

interface ClassGroup {
  name: string;
  weapons: FullWeaponInfo[];
}

/** Weapons grouped by `WeaponInfo.class` in catalog order. */
function groupByClass(weapons: readonly FullWeaponInfo[]): ClassGroup[] {
  const groups: ClassGroup[] = [];
  for (const w of weapons) {
    const group = groups.find((g) => g.name === w.class);
    if (group) group.weapons.push(w);
    else groups.push({ name: w.class, weapons: [w] });
  }
  return groups;
}

export interface WeaponGridProps {
  /** Accessible name of the radio group. */
  label: string;
  weapons: readonly FullWeaponInfo[];
  selected: WeaponId;
  unlocked: readonly WeaponId[];
  onSelect: (id: WeaponId) => void;
}

/** Radio grid of weapon cards, one column per weapon class; locked weapons are visible but inert. */
export function WeaponGrid({ label, weapons, selected, unlocked, onSelect }: WeaponGridProps) {
  return (
    <div class="loadouts__wgrid" role="radiogroup" aria-label={label}>
      {groupByClass(weapons).map((group) => (
        <div key={group.name} class="loadouts__group">
          {group.weapons.map((w) => {
            const open = unlocked.includes(w.id);
            const fire = FIRE_MODE_LABEL[w.fireMode];
            return (
              <OptionCard
                key={w.id}
                role="radio"
                class="loadouts__wcard"
                selected={w.id === selected}
                locked={!open}
                title={open ? w.description : `Unlocks at player level ${w.unlockLevel}`}
                ariaLabel={`${w.name}, ${w.class}, ${fire}${open ? "" : `, locked until player level ${w.unlockLevel}`}`}
                onClick={() => onSelect(w.id)}
              >
                <span class="loadouts__wcard-head">
                  <span class="card__title loadouts__wcard-name">{w.name}</span>
                  {!open && <LockBadge level={w.unlockLevel} />}
                </span>
                <span class="loadouts__wcard-meta">
                  <span class="loadouts__wcard-class">{w.class}</span>
                  <Badge>{fire}</Badge>
                </span>
                <MiniBars bars={w.bars} />
              </OptionCard>
            );
          })}
        </div>
      ))}
    </div>
  );
}
